import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

// Presentational URL params that must never be treated as list filters or
// forwarded to list APIs (e.g. `layout` = units grid/list view selection,
// `tab` = which profile tab is active, set by useTabNavigation's default
// paramName). Without this, switching to a tab whose list uses
// hasSearchParams fires one fetch before the URL gains `?tab=`, then a
// second once it does — read as "the same list loading twice".
export const SYSTEM_PARAMS = ["type", "reflected_to", "layout", "tab"];

const resolveExcludeParams = (excludeParams) => {
  if (!excludeParams?.length) return SYSTEM_PARAMS;
  return [...SYSTEM_PARAMS, ...excludeParams];
};

export const getSearchStateFromLocation = (
  searchString,
  excludeParams
) => {
  const excluded = resolveExcludeParams(excludeParams);
  const queryParams = new URLSearchParams(searchString || "");
  const searchParams = {};
  let page = 1;
  let hasPageInUrl = false;
  queryParams.forEach((value, key) => {
    if (key === "page") {
      page = Number(value) || 1;
      hasPageInUrl = true;
    } else if (!excluded.includes(key) && value !== "") {
      searchParams[key] = value;
    }
  });
  return { searchParams, page, hasPageInUrl };
};

const useUpdateSearch = (
  setSearchParam,
  setPage = null,
  form,
  excludeParams
) => {
  const location = useLocation();
  // Stabilize deps: default `[]` would be a new reference every render and
  // re-fire this effect → setSearchParam → infinite re-render loop.
  const excluded = useMemo(
    () => resolveExcludeParams(excludeParams),
    // Callers must pass a module-level constant (or omit) — not an inline array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeParams]
  );

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    // If queryParams is empty, don't do anything
    if (queryParams.toString() === "") {
      setSearchParam({});
      return;
    }

    const searchParams = {};
    queryParams.forEach((value, key) => {
      if (key === "page") {
        setPage && setPage(Number(value));
      } else if (!excluded.includes(key) && value !== "") {
        searchParams[key] = value;
        setPage && setPage(1);
      }
    });
    setSearchParam(searchParams);
    form && form.setFieldsValue(searchParams);
  }, [location.search, setSearchParam, setPage, form, excluded]);
};

export default useUpdateSearch;
